import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  LinearProgress,
  useTheme,
  keyframes,
  styled,
  Paper,
  Alert
} from '@mui/material';
import {
  Battery20,
  Battery30,
  Battery50,
  Battery60,
  Battery80,
  Battery90,
  BatteryFull,
  BatteryCharging20,
  BatteryCharging30,
  BatteryCharging50,
  BatteryCharging60,
  BatteryCharging80,
  BatteryCharging90,
  BatteryChargingFull,
  CheckCircle
} from '@mui/icons-material';
import apiClient from '../services/api';

// Gradient animation for title
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.info.main} 100%)`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 6s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 800,
  letterSpacing: '-0.02em',
}));

// Glassmorphism Card Style (matching ResultsPage)
const GlassCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.08)',
  padding: theme.spacing(6),
  transition: 'all 0.3s ease',
}));

// Battery pulse animation
const batteryPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
`;

const BatteryIconWrapper = styled(Box)(({ theme }) => ({
  fontSize: '120px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  animation: `${batteryPulse} 2s ease-in-out infinite`,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(4),
  '& svg': {
    fontSize: 'inherit',
  },
}));

// Progress bar with gradient
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 12,
  borderRadius: 6,
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 6,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.success.main} 100%)`,
  },
}));

interface ProgressData {
  upload_id: string;
  status: string;
  progress: number;
  message: string;
}

const BatteryLoadingPage: React.FC = () => {
  const { uploadId } = useParams<{ uploadId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [progressData, setProgressData] = useState<ProgressData>({
    upload_id: uploadId || '',
    status: 'not_started',
    progress: 0,
    message: 'Initialisierung...'
  });

  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);

  // Get battery icon based on progress
  const getBatteryIcon = (progress: number = 0, isCharging: boolean = true) => {
    if (!isCharging) {
      if (progress >= 100) return <BatteryFull />;
      if (progress >= 90) return <Battery90 />;
      if (progress >= 80) return <Battery80 />;
      if (progress >= 60) return <Battery60 />;
      if (progress >= 50) return <Battery50 />;
      if (progress >= 30) return <Battery30 />;
      return <Battery20 />;
    }

    // Charging icons
    if (progress >= 100) return <CheckCircle color="success" />;
    if (progress >= 90) return <BatteryChargingFull />;
    if (progress >= 80) return <BatteryCharging90 />;
    if (progress >= 60) return <BatteryCharging80 />;
    if (progress >= 50) return <BatteryCharging60 />;
    if (progress >= 30) return <BatteryCharging50 />;
    if (progress >= 20) return <BatteryCharging30 />;
    return <BatteryCharging20 />;
  };

  // Poll for progress updates
  useEffect(() => {
    if (!uploadId) {
      setError('Keine Upload-ID gefunden');
      return;
    }

    let pollInterval: NodeJS.Timeout;
    let pollAttempts = 0;
    const MAX_POLL_ATTEMPTS = 120; // 120 * 2s = 4 minutes max

    const pollProgress = async () => {
      try {
        pollAttempts++;
        setPollCount(pollAttempts);

        // apiClient.get() already returns response.data, not the full response
        const data: ProgressData = await apiClient.get(`/api/progress/${uploadId}`);

        setProgressData(data);

        // Navigate to results when 100% complete
        if (data.status === 'done' && data.progress === 100) {
          setTimeout(() => {
            navigate(`/results/${uploadId}`, { replace: true });
          }, 1500); // Small delay to show 100% completion before redirect

          clearInterval(pollInterval);
        }

        // Handle error status
        if (data.status === 'error') {
          setError(data.message || 'Analyse fehlgeschlagen');
          clearInterval(pollInterval);
        }

        // Stop polling after max attempts
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          setError('Zeitüberschreitung: Analyse dauert zu lange');
          clearInterval(pollInterval);
        }

      } catch (err: any) {
        console.error('Progress poll error:', err);
        console.error('Error details:', {
          type: err?.type,
          message: err?.message,
          status: err?.status,
          originalError: err?.originalError
        });

        // Don't show error immediately, wait for a few failed attempts
        if (pollAttempts > 3) {
          // Handle transformed error from API client interceptor
          const errorMsg = err?.message || err?.originalError?.message || 'Verbindung zum Server verloren';
          setError(`${errorMsg} (Poll #${pollAttempts})`);
          clearInterval(pollInterval);
        }
      }
    };

    // Initial poll
    pollProgress();

    // Poll every 2 seconds
    pollInterval = setInterval(pollProgress, 2000);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [uploadId, navigate]);

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <GlassCard elevation={0}>
          <Box textAlign="center">
            {/* Title */}
            <AnimatedTitle variant="h3" gutterBottom>
              Analyse läuft...
            </AnimatedTitle>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 6, fontWeight: 400 }}
            >
              Ihre Daten werden verarbeitet
            </Typography>

            {/* Battery Icon */}
            <BatteryIconWrapper>
              {getBatteryIcon(progressData?.progress || 0, progressData?.status !== 'done')}
            </BatteryIconWrapper>

            {/* Progress Percentage */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              {progressData?.progress || 0}%
            </Typography>

            {/* Progress Bar */}
            <StyledLinearProgress
              variant="determinate"
              value={progressData?.progress || 0}
              sx={{ mb: 3 }}
            />

            {/* Status Message */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                minHeight: '24px',
                mb: 2,
              }}
            >
              {progressData?.message || 'Initialisierung...'}
            </Typography>

            {/* Status Details */}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mb: 2 }}
            >
              Status: {progressData?.status || 'loading'}
            </Typography>

            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography variant="body2">{error}</Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Versuchen Sie, die Seite neu zu laden oder kontaktieren Sie den Support.
                </Typography>
              </Alert>
            )}

            {/* Debug Info (only in development) */}
            {process.env.NODE_ENV === 'development' && (
              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  opacity: 0.6,
                }}
              >
                {/* <Typography variant="caption" color="text.disabled">
                  Debug: Polls: {pollCount} | Upload ID: {uploadId}
                </Typography> */}
              </Box>
            )}
          </Box>
        </GlassCard>

        {/* Additional Info */}
        <Box sx={{ mt: 4, textAlign: 'center', maxWidth: '600px' }}>
          <Typography variant="body2" color="text.secondary">
            Die Analyse kann 1-3 Minuten dauern, abhängig von der Dateigröße.
            Bitte schließen Sie nicht dieses Fenster.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default BatteryLoadingPage;
